import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiCalendarCheckOutline,
  mdiCheckBold,
  mdiClockOutline,
  mdiCloseCircleOutline,
  mdiHandshakeOutline,
} from "@mdi/js"
import type { WorkOrderViewStatus } from "../../../../../../../../_shared/_logic/outreach-presentation"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../_shared/_logic/rich-tooltips"
import { dateOnlyTimestamp, parseDateOnly } from "../../../../../../../_shared/_logic/timezones"
import { formatDate, formatDateTime } from "./formatters"

export const buildDueWarning = (
  status: StatusDefinition<string> | null,
): StatusDefinition<string> | null => {
  if (!status) return null

  const labels: Record<string, string> = {
    OVERDUE: "Überfällig",
    DUE_SOON: "Bald fällig",
    AGREED_AFTER_DUE: "Termin nach Frist",
    COMPLETED_AFTER_DUE: "Nach Frist erledigt",
  }
  const label = labels[status.value]

  return label ? { ...status, label } : null
}

const buildBaseDueStatusDefinition = (dueDate?: string | null): StatusDefinition<string> | null => {
  if (!dueDate) return null

  const parsed = parseDateOnly(dueDate)

  if (!parsed) return null

  const label = formatDate(dueDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDay = new Date(parsed)
  dueDay.setHours(0, 0, 0, 0)
  const diffDays = Math.floor((dueDay.getTime() - today.getTime()) / 86400000)

  if (diffDays < 0) {
    return {
      value: "OVERDUE",
      label,
      color: "rose",
      icon: mdiCloseCircleOutline,
      description: "Gesetzliche Frist ist überschritten.",
    }
  }

  if (diffDays < 14) {
    return {
      value: "DUE_SOON",
      label,
      color: "amber",
      icon: mdiClockOutline,
      description: "Gesetzliche Frist liegt in weniger als zwei Wochen.",
    }
  }

  return {
    value: "ON_TRACK",
    label,
    color: "emerald",
    icon: mdiCheckBold,
    description: "Mehr als zwei Wochen bis zur gesetzlichen Frist.",
  }
}

const toDayTimestamp = (value?: string | null) => {
  if (!value) return null

  const dateOnlyTs = dateOnlyTimestamp(value)

  if (dateOnlyTs !== null) return dateOnlyTs

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return null

  parsed.setHours(0, 0, 0, 0)

  return parsed.getTime()
}

const buildStatusTooltip = ({
  title,
  status,
  details,
  fields = [],
}: {
  title: string
  status?: string | null
  details?: string | null
  fields?: Array<[string, unknown]>
}) =>
  buildRichTooltip([
    tooltipTitle(title),
    tooltipField("Status", status),
    tooltipText(details),
    ...fields.map(([label, value]) => tooltipField(label, value)),
  ])

const buildDueTooltip = ({
  status,
  dueLabel,
  details,
  appointmentLabel,
}: {
  status: string
  dueLabel: string
  details: string
  appointmentLabel?: string | null
}) =>
  buildStatusTooltip({
    title: "Gesetzliche Frist",
    status,
    details,
    fields: [
      ["Fällig bis", dueLabel],
      ["Termin", appointmentLabel && appointmentLabel !== "–" ? appointmentLabel : null],
    ],
  })

export const buildDueStatusPresentation = ({
  dueDate,
  statusCode,
  relevantEvent,
}: {
  dueDate?: string | null
  statusCode?: WorkOrderViewStatus | null
  relevantEvent?: ScheduleEvent | null
}): {
  definition: StatusDefinition<string> | null
  tooltip: string | null
} => {
  const base = buildBaseDueStatusDefinition(dueDate)

  if (!base || !dueDate) return { definition: null, tooltip: null }

  const dueLabel = formatDate(dueDate)
  const dueTs = toDayTimestamp(dueDate)
  const appointmentTs = toDayTimestamp(relevantEvent?.start_at)
  const appointmentLabel = formatDateTime(relevantEvent?.start_at)

  if (statusCode === "AGREED") {
    if (dueTs != null && appointmentTs != null && appointmentTs <= dueTs) {
      return {
        definition: {
          value: "AGREED_ON_TIME",
          label: dueLabel,
          color: "emerald",
          icon: mdiHandshakeOutline,
          description: "Termin ist innerhalb der Frist vereinbart.",
        },
        tooltip: buildDueTooltip({
          status: "Termin innerhalb der Frist",
          dueLabel,
          details: "Termin ist bereits vereinbart und liegt innerhalb der gesetzlichen Frist.",
          appointmentLabel,
        }),
      }
    }

    if (dueTs != null && appointmentTs != null && appointmentTs > dueTs) {
      return {
        definition: {
          value: "AGREED_AFTER_DUE",
          label: dueLabel,
          color: "amber",
          icon: mdiHandshakeOutline,
          description: "Termin ist vereinbart, liegt aber nach der Frist.",
        },
        tooltip: buildDueTooltip({
          status: "Termin nach Frist",
          dueLabel,
          details: "Termin ist bereits vereinbart, liegt aber nach der gesetzlichen Frist.",
          appointmentLabel,
        }),
      }
    }

    return {
      definition: base,
      tooltip: buildDueTooltip({
        status: "Termin vereinbart",
        dueLabel,
        details: "Termin ist bereits vereinbart.",
        appointmentLabel,
      }),
    }
  }

  if (statusCode === "COMPLETED") {
    if (dueTs != null && appointmentTs != null && appointmentTs <= dueTs) {
      return {
        definition: {
          value: "COMPLETED_ON_TIME",
          label: dueLabel,
          color: "emerald",
          icon: mdiCalendarCheckOutline,
          description: "Auftrag wurde innerhalb der Frist erledigt.",
        },
        tooltip: buildDueTooltip({
          status: "Fristgerecht erledigt",
          dueLabel,
          details: "Auftrag wurde innerhalb der gesetzlichen Frist erledigt.",
          appointmentLabel,
        }),
      }
    }

    if (dueTs != null && appointmentTs != null && appointmentTs > dueTs) {
      return {
        definition: {
          value: "COMPLETED_AFTER_DUE",
          label: dueLabel,
          color: "rose",
          icon: mdiCloseCircleOutline,
          description: "Auftrag wurde nach der Frist erledigt.",
        },
        tooltip: buildDueTooltip({
          status: "Nach Frist erledigt",
          dueLabel,
          details: "Auftrag wurde erst nach der gesetzlichen Frist erledigt.",
          appointmentLabel,
        }),
      }
    }

    return {
      definition: {
        value: "COMPLETED",
        label: dueLabel,
        color: "emerald",
        icon: mdiCalendarCheckOutline,
        description: "Auftrag ist bereits erledigt.",
      },
      tooltip: buildDueTooltip({
        status: "Erledigt",
        dueLabel,
        details: "Auftrag ist bereits erledigt.",
        appointmentLabel,
      }),
    }
  }

  if (statusCode === "FAILED") {
    return {
      definition: {
        value: "FAILED",
        label: dueLabel,
        color: "rose",
        icon: mdiCloseCircleOutline,
        description:
          base.value === "OVERDUE"
            ? "Frist ist überschritten und der Auftrag wurde erfolglos beendet."
            : "Auftrag wurde erfolglos beendet.",
      },
      tooltip: buildDueTooltip({
        status: "Erfolglos beendet",
        dueLabel,
        details:
          base.value === "OVERDUE"
            ? "Die Frist ist überschritten und der Auftrag wurde erfolglos beendet."
            : "Auftrag wurde erfolglos beendet.",
        appointmentLabel,
      }),
    }
  }

  return {
    definition: base,
    tooltip: buildDueTooltip({
      status:
        base.value === "OVERDUE"
          ? "Frist überschritten"
          : base.value === "DUE_SOON"
            ? "Bald fällig"
            : "In Frist",
      dueLabel,
      details: base.description ?? "Gesetzliche Frist hinterlegt.",
      appointmentLabel,
    }),
  }
}
